import React, { useState, useEffect, useCallback } from 'react';
import { Home, RefreshCw, Play, Pause, House, AlertTriangle, Clock, Ruler } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useMaisonApi } from '../components/Maison/useMaisonApi';
import { MapCard } from '../components/Maison/MapCard';
import { SettingsCard } from '../components/Maison/SettingsCard';
import { MaintenanceCard } from '../components/Maison/MaintenanceCard';
import { TechCard, DiagnosticCard } from '../components/Maison/InfoCards';

const POLL_MS = 5000;

const batteryColor = (pct) => (pct < 20 ? '#ef4444' : pct < 40 ? '#f59e0b' : '#22c55e');

function BatteryRing({ value, color, running, children, size = 220, stroke = 14 }) {
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const pct = Math.max(0, Math.min(100, value ?? 0));
    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg width={size} height={size} className={`-rotate-90 ${running ? 'animate-pulse' : ''}`}>
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="hsl(var(--muted))" strokeWidth={stroke} />
                <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
                    strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c - (pct / 100) * c}
                    style={{ transition: 'stroke-dashoffset .6s ease, stroke .6s ease' }} />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">{children}</div>
        </div>
    );
}

export function Maison() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const canOperate = user?.role !== 'viewer';
    const request = useMaisonApi();

    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const apply = useCallback((d) => {
        if (d.error) { setError(d.error); return; }
        setError(null);
        setData(d);
    }, []);

    const fetchStatus = useCallback(async () => {
        try {
            const res = await request('');
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            apply(await res.json());
        } catch { setError('maison_unreachable'); }
    }, [request, apply]);

    useEffect(() => {
        const first = setTimeout(fetchStatus, 0);
        const id = setInterval(() => {
            if (document.visibilityState === 'visible') fetchStatus();
        }, POLL_MS);
        return () => { clearTimeout(first); clearInterval(id); };
    }, [fetchStatus]);

    const act = async (path, body, failCode) => {
        setBusy(true);
        try {
            const res = await request(path, 'POST', body);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            apply(await res.json());
        } catch { setError(failCode); }
        setBusy(false);
    };

    const errorText = (code) => {
        if (code === 'commandFailed' || code === 'settingFailed') return t(`maison.${code}`);
        if (['maison_unreachable', 'maison_bad_response', 'robot_unreachable', 'robot_busy'].includes(code)) return t(`maison.errors.${code}`);
        return t('maison.errors.generic', { message: code });
    };

    if (!data && !error) return (
        <div className="flex items-center justify-center h-64 text-muted-foreground gap-2">
            <RefreshCw className="w-5 h-5 animate-spin" />{t('maison.loading')}
        </div>
    );

    const faults = data?.faults ?? [];
    const battery = data?.battery;

    return (
        <div className="max-w-xl mx-auto w-full px-1 sm:px-0 pb-8">
            <div className="mb-6">
                <h2 className="text-3xl font-bold tracking-tight flex items-center gap-3">
                    <Home className="w-7 h-7 text-primary" />{t('nav.maison')}
                </h2>
                <p className="text-muted-foreground mt-2">{t('maison.subtitle')}</p>
            </div>

            {error && (
                <div className="mb-4 flex items-start gap-2 rounded-xl border border-red-500/40 bg-red-500/10 text-red-600 dark:text-red-400 px-4 py-3 text-sm">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{errorText(error)}
                </div>
            )}

            {data && (
                <>
                    <div className="flex justify-center my-6">
                        <BatteryRing value={battery} color={batteryColor(battery ?? 0)} running={data.running}>
                            <span className="text-5xl font-bold tabular-nums leading-none">{battery ?? '–'}{battery != null && ' %'}</span>
                            <span className="text-muted-foreground mt-2 text-sm">{t(`maison.states.${data.status}`, { defaultValue: data.status })}</span>
                        </BatteryRing>
                    </div>

                    {data.map_state && (
                        <p className="text-center text-xs text-muted-foreground -mt-2 mb-4">
                            {t('maison.mapStateLabel')} : {t(`maison.mapStates.${data.map_state}`, { defaultValue: data.map_state })}
                        </p>
                    )}

                    {(data.clean_time || data.clean_area) && (
                        <div className="flex justify-center gap-6 text-sm text-muted-foreground mb-6">
                            {data.clean_time ? <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{t('maison.minutes', { n: data.clean_time })}</span> : null}
                            {data.clean_area ? <span className="flex items-center gap-1.5"><Ruler className="w-4 h-4" />{t('maison.area', { n: data.clean_area })}</span> : null}
                        </div>
                    )}

                    {canOperate && (
                        <div className="grid grid-cols-3 gap-3 mb-6">
                            <button disabled={busy} onClick={() => act('/start', null, 'commandFailed')}
                                className="flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground py-3 font-medium disabled:opacity-50">
                                <Play className="w-4 h-4" />{t('maison.start')}
                            </button>
                            <button disabled={busy} onClick={() => act('/pause', null, 'commandFailed')}
                                className="flex items-center justify-center gap-2 rounded-xl bg-card border border-border py-3 font-medium hover:bg-accent disabled:opacity-50">
                                <Pause className="w-4 h-4" />{t('maison.pause')}
                            </button>
                            <button disabled={busy} onClick={() => act('/base', null, 'commandFailed')}
                                className="flex items-center justify-center gap-2 rounded-xl bg-card border border-border py-3 font-medium hover:bg-accent disabled:opacity-50">
                                <House className="w-4 h-4" />{t('maison.base')}
                            </button>
                        </div>
                    )}

                    {faults.length > 0 && (
                        <div className="mb-6 rounded-xl border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 px-4 py-3 text-sm space-y-1">
                            {faults.map(f => (
                                <div key={f} className="flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                                    {t(`maison.faults.${f}`, { defaultValue: f })}
                                </div>
                            ))}
                        </div>
                    )}

                    <MapCard request={request} />

                    <SettingsCard
                        settings={data.settings}
                        suction={data.suction}
                        canOperate={canOperate}
                        busy={busy}
                        onSet={(name, value) => act(`/setting/${name}`, { value }, 'settingFailed')}
                        onSuction={(level) => act(`/${level}`, null, 'commandFailed')}
                    />

                    <MaintenanceCard
                        items={data.maintenance}
                        canOperate={canOperate}
                        busy={busy}
                        onReset={(key) => act(`/reset/${key}`, null, 'commandFailed')}
                    />

                    <TechCard tech={data.tech} />
                    <DiagnosticCard diagnostic={data.diagnostic} />
                </>
            )}
        </div>
    );
}
